import type {
  CategorySearchFilters,
  CategorySearchResults,
  CategoryCardPayload,
  CategoryPrintRow,
  CategorySearchRow,
  CategoryEnrichmentPreview,
  CategoryEnrichmentApplyResult,
  CategoryEnrichmentApplyPayload,
  CategoryEnrichmentAttributes,
  CategoryEnrichmentMappingInput,
  CategoryEnrichmentJunkPayload,
  DeleteCategoryEntryParams,
  DeleteCategoryEntryResult
} from '../../types/rpc-contracts';
import { callRpc, createRpcError } from './base';
import { ensureArray, ensureObject } from './util';
import { isRecord } from '../../utils/typeGuards';

type CategoryPrintJobParams = {
  filters?: CategorySearchFilters;
  selectedIds?: string[];
};

export const searchCategoriesAdvanced = async (
  filters: CategorySearchFilters = {}
): Promise<CategorySearchResults> => {
  return await callRpc('search_categories_advanced', {
    params: { filters },
    transform: (data) =>
      ensureObject<CategorySearchResults>(
        data,
        'INVALID_CATEGORY_SEARCH_RESPONSE',
        'Category search returned invalid response format',
        { data, filters }
      ),
    successMeta: (result) => ({ resultCount: result.rows?.length ?? 0 })
  });
};

export const searchCategoriesSemantic = async (
  embedding: number[],
  filters: Omit<CategorySearchFilters, 'text_search' | 'search_mode'> = {}
): Promise<CategorySearchResults> => {
  return await callRpc('search_categories_semantic', {
    params: {
      p_search_embedding: JSON.stringify(embedding),
      p_limit: filters.limit ?? 50,
      p_entry_fee_min: filters.entry_fee_min ?? null,
      p_entry_fee_max: filters.entry_fee_max ?? null,
      p_has_bisac: filters.has_bisac ?? null
    },
    transform: (data) => ({
      rows: ensureArray(
        data,
        'INVALID_CATEGORY_SEARCH_RESPONSE',
        'Category semantic search returned invalid response',
        { data, filters }
      ),
      total_count: Array.isArray(data) ? data.length : 0,
      applied_filters: { ...filters, search_mode: 'semantic' },
      query_performance: {
        execution_time_ms: 0,
        cache_hit: false
      }
    }),
    successMeta: (result, durationMs) => {
      result.query_performance.execution_time_ms = durationMs;
      return { resultCount: result.rows.length };
    }
  });
};

export const searchCategoriesHybrid = async (
  text: string,
  embedding: number[],
  ftsWeight: number = 0.6,
  semanticWeight: number = 0.4,
  filters: Omit<CategorySearchFilters, 'search_mode'> = {}
): Promise<CategorySearchResults> => {
  return await callRpc('search_categories_hybrid', {
    params: {
      p_search_term: text,
      p_search_embedding: JSON.stringify(embedding),
      p_fts_weight: ftsWeight,
      p_semantic_weight: semanticWeight,
      p_limit: filters.limit ?? 50,
      p_entry_fee_min: filters.entry_fee_min ?? null,
      p_entry_fee_max: filters.entry_fee_max ?? null,
      p_has_bisac: filters.has_bisac ?? null
    },
    transform: (data) => ({
      rows: ensureArray(
        data,
        'INVALID_CATEGORY_SEARCH_RESPONSE',
        'Category hybrid search returned invalid response',
        { data, filters, text }
      ),
      total_count: Array.isArray(data) ? data.length : 0,
      applied_filters: { ...filters, text_search: text, search_mode: 'hybrid' },
      query_performance: {
        execution_time_ms: 0,
        cache_hit: false
      }
    }),
    successMeta: (result, durationMs) => {
      result.query_performance.execution_time_ms = durationMs;
      return { resultCount: result.rows.length };
    }
  });
};

export const getCategoryCard = async (
  categoryId: string
): Promise<CategoryCardPayload> => {
  return await callRpc('get_category_card', {
    params: { p_category_id: categoryId },
    transform: (data) =>
      ensureObject<CategoryCardPayload>(
        data,
        'INVALID_CATEGORY_CARD_RESPONSE',
        'Category card returned invalid response',
        { data, categoryId }
      )
  });
};

interface LegacyCategorySearchResponse {
  rows?: CategorySearchRow[];
}

export interface FilteredCategorySearchParams {
  query?: string | null;
  has_bisac?: boolean | undefined;
  deadline_structure?: string[] | null;
  entry_fee_min?: number | null;
  entry_fee_max?: number | null;
  sort_by?: string;
  sort_dir?: string;
  limit?: number;
  offset?: number;
}

export const searchCategoriesFiltered = async (
  params: FilteredCategorySearchParams
): Promise<CategorySearchRow[]> => {
  return await callRpc('search_categories_filtered', {
    params: {
      query: params.query ?? null,
      has_bisac: params.has_bisac,
      deadline_structure: params.deadline_structure ?? null,
      entry_fee_min: params.entry_fee_min ?? null,
      entry_fee_max: params.entry_fee_max ?? null,
      sort_by: params.sort_by ?? 'program_name',
      sort_dir: params.sort_dir ?? 'asc',
      limit: params.limit ?? 50,
      offset: params.offset ?? 0
    },
    transform: (data) => {
      if (Array.isArray(data)) {
        return data as CategorySearchRow[];
      }
      if (isRecord(data) && Array.isArray((data as LegacyCategorySearchResponse).rows)) {
        return (data as LegacyCategorySearchResponse).rows ?? [];
      }
      return [];
    }
  });
};

export interface CategoryDetailsPayload {
  [key: string]: unknown;
}

export interface UpdateCategoryBucketsParams {
  categoryId: string;
  bucketLevel1?: string | null;
  bucketLevel2?: string | null;
  editorId?: string | null;
}

export interface UpdateCategoryBucketsResult {
  success: boolean;
  updated: boolean;
  category_id: string;
  bucket_level_1: string | null;
  bucket_level_2: string | null;
  updated_at?: string;
}

export interface UpdateCategoryDisplayParams {
  categoryId: string;
  displayName?: string | null;
  description?: string | null;
  editorId?: string | null;
}

export interface UpdateCategoryDisplayResult {
  success: boolean;
  updated: boolean;
  category_id: string;
  category_display_name: string | null;
  category_description: string | null;
  updated_at?: string;
}

export interface UpdateCategoryQcFlagParams {
  categoryId: string;
  flag: boolean;
  notes?: string | null;
  editorId?: string | null;
}

export interface UpdateCategoryQcFlagResult {
  success: boolean;
  updated: boolean;
  category_id: string;
  qc_flag_categories: boolean;
  qc_flag_categories_notes: string | null;
  updated_at?: string;
}

export interface CategoryEnrichmentPreviewOptions {
  minConfidence?: number;
  limit?: number;
}

export interface CategoryEnrichmentApplyParams {
  categoryId: string;
  attributes?: CategoryEnrichmentAttributes | null;
  mappings?: CategoryEnrichmentMappingInput[] | null;
  junk?: CategoryEnrichmentJunkPayload | null;
  editorId?: string | null;
}

export const getCategoryDetails = async (
  categoryId: string
): Promise<CategoryDetailsPayload> => {
  return await callRpc('get_category_details', {
    params: { category_id: categoryId },
    transform: (data) =>
      ensureObject<CategoryDetailsPayload>(
        data,
        'INVALID_CATEGORY_DETAILS',
        'Category details RPC returned invalid response',
        { categoryId }
      )
  });
};

export const createCategoryPrintJob = async ({
  filters = {},
  selectedIds
}: CategoryPrintJobParams = {}): Promise<string> => {
  const { limit: _unusedLimit, offset: _unusedOffset, ...restFilters } = filters ?? {};
  void _unusedLimit;
  void _unusedOffset;

  return await callRpc('create_print_job_cat', {
    params: {
      p_filters: restFilters,
      p_selected_ids: selectedIds && selectedIds.length > 0 ? selectedIds : null
    },
    transform: (data) => {
      if (!data || typeof data !== 'string') {
        throw createRpcError(
          'INVALID_CATEGORY_PRINT_JOB_RESPONSE',
          'Print job RPC returned invalid response',
          { data, filters, selectedIds }
        );
      }
      return data;
    }
  });
};

export const getCategoryPrintResults = async (
  jobId: string
): Promise<CategoryPrintRow[]> => {
  return await callRpc('get_category_print_results', {
    params: { p_job_id: jobId },
    transform: (data) =>
      ensureArray<CategoryPrintRow>(
        data,
        'INVALID_CATEGORY_PRINT_RESPONSE',
        'Category print results returned invalid response',
        { data, jobId }
      ),
    successMeta: (result) => ({ resultCount: result.length })
  });
};

export const updateCategoryBuckets = async (
  params: UpdateCategoryBucketsParams
): Promise<UpdateCategoryBucketsResult> => {
  return await callRpc('update_category_buckets', {
    params: {
      p_category_id: params.categoryId,
      p_bucket_level_1: params.bucketLevel1 ?? null,
      p_bucket_level_2: params.bucketLevel2 ?? null,
      p_editor_id: params.editorId ?? null
    },
    transform: (data) =>
      ensureObject<UpdateCategoryBucketsResult>(
        data,
        'INVALID_CATEGORY_BUCKET_UPDATE',
        'update_category_buckets RPC returned invalid response',
        { params }
      )
  });
};

export const updateCategoryDisplayInfo = async (
  params: UpdateCategoryDisplayParams
): Promise<UpdateCategoryDisplayResult> => {
  return await callRpc('update_category_display_info', {
    params: {
      p_category_id: params.categoryId,
      p_display_name: params.displayName ?? null,
      p_description: params.description ?? null,
      p_editor_id: params.editorId ?? null
    },
    transform: (data) =>
      ensureObject<UpdateCategoryDisplayResult>(
        data,
        'INVALID_CATEGORY_DISPLAY_UPDATE',
        'update_category_display_info RPC returned invalid response',
        { params }
      )
  });
};

export const updateCategoryQcFlag = async (
  params: UpdateCategoryQcFlagParams
): Promise<UpdateCategoryQcFlagResult> => {
  return await callRpc('update_category_qc_flag', {
    params: {
      p_category_id: params.categoryId,
      p_flag: params.flag,
      p_notes: params.notes ?? null,
      p_editor_id: params.editorId ?? null
    },
    transform: (data) =>
      ensureObject<UpdateCategoryQcFlagResult>(
        data,
        'INVALID_CATEGORY_QC_FLAG_UPDATE',
        'update_category_qc_flag RPC returned invalid response',
        { params }
      )
  });
};

export const getCategoryEnrichmentPreview = async (
  categoryId: string,
  options: CategoryEnrichmentPreviewOptions = {}
): Promise<CategoryEnrichmentPreview> => {
  return await callRpc('category_enrichment_preview', {
    params: {
      p_category_id: categoryId,
      p_min_confidence: options.minConfidence ?? null,
      p_limit: options.limit ?? null
    },
    transform: (data) =>
      ensureObject<CategoryEnrichmentPreview>(
        data,
        'INVALID_CATEGORY_ENRICHMENT_PREVIEW',
        'category_enrichment_preview RPC returned invalid response',
        { categoryId, options }
      )
  });
};

export const applyCategoryEnrichment = async (
  params: CategoryEnrichmentApplyParams
): Promise<CategoryEnrichmentApplyResult> => {
  const payload: CategoryEnrichmentApplyPayload = {
    attributes: params.attributes ?? null,
    mappings: params.mappings ?? null,
    junk: params.junk ?? null,
    editor_id: params.editorId ?? null
  };

  return await callRpc('category_enrichment_apply', {
    params: {
      p_category_id: params.categoryId,
      p_attributes: payload.attributes,
      p_mappings: payload.mappings,
      p_junk: payload.junk,
      p_editor_id: payload.editor_id ?? null
    },
    transform: (data) =>
      ensureObject<CategoryEnrichmentApplyResult>(
        data,
        'INVALID_CATEGORY_ENRICHMENT_APPLY',
        'category_enrichment_apply RPC returned invalid response',
        { params }
      )
  });
};

export const deleteCategoryEntry = async (
  params: DeleteCategoryEntryParams
): Promise<DeleteCategoryEntryResult> => {
  if (!params.categoryId) {
    throw createRpcError('INVALID_CATEGORY_ID', 'Category ID is required for deletion');
  }

  return await callRpc('delete_category_entry', {
    params: {
      p_category_id: params.categoryId,
      p_reason: params.reason ?? null,
      p_editor_id: params.editorId ?? null
    },
    transform: (data) =>
      ensureObject<DeleteCategoryEntryResult>(
        data,
        'INVALID_CATEGORY_DELETE_RESPONSE',
        'delete_category_entry RPC returned invalid response',
        { params }
      )
  });
};

export interface SyncJudgingCategoryCountsResult {
  updated_rows: number;
}

export const syncJudgingCategoryCounts = async (): Promise<SyncJudgingCategoryCountsResult> => {
  return await callRpc('sync_judging_category_counts', {
    transform: (data) => {
      if (!isRecord(data) || typeof data.updated_rows !== 'number') {
        throw createRpcError(
          'INVALID_SYNC_JUDGING_RESPONSE',
          'Failed to sync judging category counts: invalid response format',
          { data }
        );
      }
      return { updated_rows: data.updated_rows };
    },
    successMeta: (result) => ({ updatedRows: result.updated_rows })
  });
};

export interface BucketStatsSummary {
  total_categories: number;
  categories_with_bucket_level_1: number;
  categories_with_bucket_level_2: number;
  bucket_level_1_coverage_pct: number;
  bucket_level_2_coverage_pct: number;
}

export interface BucketLevel1Stats {
  bucket_level_1: string;
  category_count: number;
  program_count: number;
  with_level_2: number;
  level_2_coverage_pct: number;
}

export interface BucketLevel2Stats {
  bucket_level_1: string;
  bucket_level_2: string;
  category_count: number;
  program_count: number;
}

export interface BucketStatisticsResult {
  summary: BucketStatsSummary;
  by_bucket_level_1: BucketLevel1Stats[];
  by_bucket_level_2: BucketLevel2Stats[];
  generated_at: string;
}

export const getBucketStatistics = async (): Promise<BucketStatisticsResult> => {
  return await callRpc('get_bucket_statistics', {
    transform: (data) =>
      ensureObject<BucketStatisticsResult>(
        data,
        'INVALID_BUCKET_STATS_RESPONSE',
        'get_bucket_statistics RPC returned invalid response',
        { data }
      )
  });
};

export type {
  DeleteCategoryEntryParams,
  DeleteCategoryEntryResult
};
